import { calendarWithKoreanHolidays } from "./calendar";
import { sampleRobotDocumentLinks, sampleRobotDocuments } from "./sample-documents";
import type {
  CostOptions,
  Dependency,
  DependencyType,
  ExecutionTask,
  Project,
  ProjectRecord,
  Task,
} from "./types";

function costOptions(): CostOptions {
  return {
    workingDaysPerMonth: 21,
    includeOverhead: true,
    overheadRatio: 1.1,
    includeTechnicalFee: true,
    technicalFeeRatio: 0.2,
    hideRatesByDefault: false,
  };
}

function exec(id: string, title: string, extra: Partial<ExecutionTask>): ExecutionTask {
  const t0 = "2026-10-08T09:00:00.000Z";
  return {
    id,
    title,
    assignees: [],
    linkedTaskIds: [],
    labelIds: [],
    checklist: [],
    attachments: [],
    comments: [],
    activity: [],
    customFields: {},
    order: 0,
    createdAt: t0,
    updatedAt: t0,
    ...extra,
  };
}

/** AI 로봇 생산 공장 — 설비·비전·양산 이관, 대형 하드웨어 예산. */
export function createSampleRobotProject(): Project {
  const tasks: Task[] = [];
  const deps: Dependency[] = [];
  const add = (key: string, parent: string | null, name: string, duration: number, extra: Partial<Task> = {}) => {
    tasks.push({ id: key, parentId: parent, name, duration, percentComplete: 0, progressMethod: "percent", ...extra });
    return key;
  };
  let dseq = 0;
  const link = (p: string, s: string, type: DependencyType = "FS", lag = 0) => {
    dseq++;
    deps.push({ id: `rob_d${dseq}`, predecessorId: p, successorId: s, type, lag });
  };

  add("rob_p1", null, "기획·타당성", 0, { percentComplete: 80 });
  add("rob_t1-1", "rob_p1", "공장 레이아웃·물동 요구분석", 8, {
    percentComplete: 100,
    assignee: "한로봇",
    assignedResourceId: "res_han_robot",
    plannedMM: 0.4,
    deliverable: "레이아웃 요구서 v1",
    actualCost: 28_000_000,
  });
  add("rob_t1-2", "rob_p1", "로봇 셀 구성안 및 안전 구역", 10, {
    percentComplete: 100,
    assignee: "한로봇",
    assignedResourceId: "res_han_robot",
    plannedMM: 0.5,
    actualCost: 36_000_000,
  });
  add("rob_t1-3", "rob_p1", "투자 타당성·원가 시뮬레이션", 6, {
    percentComplete: 70,
    assignee: "신자문",
    assignedResourceId: "res_shin_spec",
    plannedMM: 0.2,
    actualCost: 22_000_000,
  });
  add("rob_m1", "rob_p1", "착수 승인", 0, { percentComplete: 100 });
  link("rob_t1-1", "rob_t1-2");
  link("rob_t1-1", "rob_t1-3", "SS", 3);
  link("rob_t1-2", "rob_m1");
  link("rob_t1-3", "rob_m1");

  add("rob_p2", null, "로봇·비전 설계", 0, { percentComplete: 45 });
  add("rob_t2-1", "rob_p2", "매니퓰레이터 사양·도달 반경", 8, {
    percentComplete: 80,
    assignee: "백제어",
    assignedResourceId: "res_baek_ctrl",
    plannedMM: 0.4,
    actualCost: 40_000_000,
  });
  add("rob_t2-2", "rob_p2", "비전 검사 모델 설계", 12, {
    percentComplete: 50,
    assignee: "나비전",
    assignedResourceId: "res_na_vision",
    plannedMM: 0.6,
    actualCost: 24_000_000,
  });
  add("rob_t2-3", "rob_p2", "제어 시퀀스·인터락", 10, {
    percentComplete: 30,
    assignee: "백제어",
    assignedResourceId: "res_baek_ctrl",
    plannedMM: 0.5,
    actualCost: 12_000_000,
  });
  add("rob_t2-4", "rob_p2", "설계 리뷰 및 안전 인증 체크", 3, {
    percentComplete: 0,
    assignee: "한로봇",
    assignedResourceId: "res_han_robot",
    plannedMM: 0.15,
  });
  add("rob_m2", "rob_p2", "설계 승인", 0);
  link("rob_m1", "rob_t2-1");
  link("rob_t2-1", "rob_t2-2", "SS", 2);
  link("rob_t2-1", "rob_t2-3", "SS", 4);
  link("rob_t2-2", "rob_t2-4");
  link("rob_t2-3", "rob_t2-4");
  link("rob_t2-4", "rob_m2");

  add("rob_p3", null, "라인 구축", 0, { percentComplete: 15 });
  add("rob_t3-1", "rob_p3", "로봇 암 조달·설치", 40, {
    percentComplete: 20,
    assignee: "한로봇",
    assignedResourceId: "res_han_robot",
    plannedMM: 0.9,
    actualCost: 48_000_000,
  });
  add("rob_t3-2", "rob_p3", "비전 카메라 리그·조명", 25, {
    percentComplete: 10,
    assignee: "나비전",
    assignedResourceId: "res_na_vision",
    plannedMM: 0.6,
  });
  add("rob_t3-3", "rob_p3", "MES 연동 및 실적 인터페이스", 20, {
    percentComplete: 15,
    assignee: "이아키",
    assignedResourceId: "res_lee_arch",
    plannedMM: 0.4,
  });
  add("rob_t3-4", "rob_p3", "엣지 게이트웨이·클라우드 파이프라인", 16, {
    percentComplete: 25,
    assignee: "강인프라",
    assignedResourceId: "res_kang_infra",
    plannedMM: 0.5,
    actualCost: 8_000_000,
  });
  link("rob_m2", "rob_t3-1");
  link("rob_t3-1", "rob_t3-2", "SS", 5);
  link("rob_t3-1", "rob_t3-3", "SS", 6);
  link("rob_t3-3", "rob_t3-4", "SS", 3);

  add("rob_p4", null, "시운전·양산 이관", 0);
  add("rob_t4-1", "rob_p4", "파일럿 셀 시운전", 20, {
    assignee: "백제어",
    assignedResourceId: "res_baek_ctrl",
    plannedMM: 0.5,
  });
  add("rob_t4-2", "rob_p4", "수율 튜닝·불량 분류 재학습", 20, {
    assignee: "윤데이터",
    assignedResourceId: "res_yoon_data",
    plannedMM: 0.5,
  });
  add("rob_t4-3", "rob_p4", "작업자 교육 및 SOP", 8, {
    assignee: "오품질",
    assignedResourceId: "res_oh_qa",
    plannedMM: 0.3,
  });
  add("rob_m4", "rob_p4", "양산 승인", 0, { deadline: "2027-04-30" });
  link("rob_t3-2", "rob_t4-1");
  link("rob_t3-4", "rob_t4-1");
  link("rob_t4-1", "rob_t4-2", "SS", 3);
  link("rob_t4-2", "rob_t4-3");
  link("rob_t4-3", "rob_m4");

  add("rob_p5", null, "안정화", 0);
  add("rob_t5-1", "rob_p5", "양산 지원 및 장애 대응", 25, {
    assignee: "한로봇",
    assignedResourceId: "res_han_robot",
    plannedMM: 0.7,
  });
  add("rob_m5", "rob_p5", "프로젝트 종료", 0);
  link("rob_m4", "rob_t5-1");
  link("rob_t5-1", "rob_m5");

  return {
    id: "sample-robot",
    name: "AI 로봇 생산 공장",
    start: "2026-07-01",
    statusDate: "2026-10-20",
    calendar: calendarWithKoreanHolidays(),
    tasks,
    dependencies: deps,
    baselines: [],
    activeBaselineId: null,
    criticalThreshold: 0,
    executionTasks: [
      exec("rob_x1", "파일럿 셀 안전 인증 체크리스트 초안", {
        status: "inProgress",
        priority: "urgent",
        bucketId: "b_week",
        assignees: ["한로봇"],
        start: "2026-10-13",
        due: "2026-10-24",
        linkedTaskIds: ["rob_t2-4"],
        labelIds: ["l_safety"],
        checklist: [
          { id: "rob_ck1", text: "인터락 매트릭스 초안", done: true },
          { id: "rob_ck2", text: "안전펜스 높이 규격 확인", done: false },
        ],
        order: 0,
      }),
      exec("rob_x2", "비전 불량 샘플 2천 장 라벨링", {
        status: "notStarted",
        priority: "important",
        bucketId: "b_todo",
        assignees: ["나비전", "서머신"],
        due: "2026-11-07",
        linkedTaskIds: ["rob_t2-2"],
        labelIds: ["l_vision"],
        order: 0,
      }),
      exec("rob_x3", "1호기 암 납기 벤더 확인", {
        status: "blocked",
        priority: "urgent",
        bucketId: "b_review",
        assignees: ["한로봇"],
        due: "2026-10-18",
        linkedTaskIds: ["rob_t3-1"],
        labelIds: ["l_hw"],
        notes: "하반기 슬롯 부족. 대체 기종 검토 중.",
        order: 0,
      }),
      exec("rob_x4", "킥오프 투자심의 자료 배포", {
        status: "done",
        priority: "low",
        bucketId: "b_done",
        assignees: ["신자문"],
        start: "2026-07-03",
        due: "2026-07-08",
        completedAt: "2026-07-07T08:00:00.000Z",
        linkedTaskIds: ["rob_t1-3"],
        labelIds: ["l_doc"],
        order: 0,
      }),
      exec("rob_x_me_safety", "안전 인증 일정 조율", {
        status: "inProgress",
        priority: "important",
        bucketId: "b_week",
        assignees: ["나"],
        start: "2026-10-13",
        due: "2026-10-16",
        linkedTaskIds: ["rob_t2-4"],
        labelIds: ["l_safety"],
        notes: "안전펜스 높이 확인이 막혀 인증 일정이 밀리고 있다.",
        comments: [{ id: "rob_cm_me1", author: "한로봇", body: "필츠 회신은 10/22로 다시 잡혔습니다.", at: "2026-10-16T02:00:00.000Z" }],
        order: 0,
      }),
      exec("rob_x_me_cell", "파일럿 셀 진척 점검", {
        status: "notStarted",
        priority: "urgent",
        bucketId: "b_week",
        assignees: ["나"],
        start: "2026-10-19",
        due: "2026-10-19",
        linkedTaskIds: ["rob_t4-1"],
        labelIds: ["l_hw"],
        order: 0,
      }),
      exec("rob_x_me_arm", "암 납기 리스크를 현황에 반영", {
        status: "inProgress",
        priority: "important",
        bucketId: "b_week",
        assignees: ["나"],
        start: "2026-10-20",
        due: "2026-10-22",
        linkedTaskIds: ["rob_t3-1"],
        labelIds: ["l_hw"],
        checklist: [
          { id: "rob_ck_me1", text: "대체 기종 견적", done: false },
          { id: "rob_ck_me2", text: "파일럿 1셀 선행 여부", done: true },
        ],
        order: 1,
      }),
      exec("rob_x_vision_week", "불량 샘플 라벨 기준 합의", {
        status: "notStarted",
        priority: "important",
        bucketId: "b_week",
        assignees: ["나비전"],
        due: "2026-10-23",
        linkedTaskIds: ["rob_t2-2"],
        labelIds: ["l_vision"],
        order: 1,
      }),
      exec("rob_x_me_gate", "양산 게이트 자료 목차", {
        status: "notStarted",
        priority: "low",
        bucketId: "b_todo",
        assignees: ["나"],
        due: "2026-11-12",
        linkedTaskIds: ["rob_m4"],
        labelIds: ["l_doc"],
        order: 0,
      }),
    ],
    planner: {
      buckets: [
        { id: "b_todo", name: "백로그" },
        { id: "b_week", name: "이번 주 현장" },
        { id: "b_review", name: "인증·검토" },
        { id: "b_done", name: "완료" },
      ],
      labels: [
        { id: "l_hw", name: "설비", color: "orange" },
        { id: "l_vision", name: "비전", color: "teal" },
        { id: "l_safety", name: "안전", color: "red" },
        { id: "l_doc", name: "문서", color: "blue" },
      ],
      customFields: [],
    },
    records: sampleRobotRecords(),
    documents: sampleRobotDocuments(),
    documentLinks: sampleRobotDocumentLinks(),
    resourceAssignments: [
      {
        id: "asg_rob_han",
        resourceId: "res_han_robot",
        projectRole: "로봇 라인 총괄 PM",
        accessRole: "pm",
        startDate: "2026-07-01",
        endDate: "2027-05-31",
        allocationRatio: 1.0,
        plannedMM: 11.0,
      },
      {
        id: "asg_rob_me",
        resourceId: "res_me",
        projectRole: "사업 소유자",
        accessRole: "admin",
        startDate: "2026-07-01",
        endDate: "2027-05-31",
        allocationRatio: 0.2,
        plannedMM: 2.2,
      },
      {
        id: "asg_rob_na",
        resourceId: "res_na_vision",
        projectRole: "비전 검사 모델 PL",
        accessRole: "pl",
        startDate: "2026-08-01",
        endDate: "2027-04-30",
        allocationRatio: 1.0,
        plannedMM: 9.0,
      },
      {
        id: "asg_rob_baek",
        resourceId: "res_baek_ctrl",
        projectRole: "제어·시운전 (협력사)",
        accessRole: "pl",
        startDate: "2026-08-01",
        endDate: "2027-04-15",
        allocationRatio: 0.8,
        plannedMM: 6.8,
      },
      {
        id: "asg_rob_kang",
        resourceId: "res_kang_infra",
        projectRole: "엣지·클라우드 파이프라인",
        accessRole: "member",
        startDate: "2026-09-01",
        endDate: "2027-02-28",
        allocationRatio: 0.6,
        plannedMM: 3.6,
      },
      {
        id: "asg_rob_lee",
        resourceId: "res_lee_arch",
        projectRole: "MES 연동 아키텍처 자문",
        accessRole: "member",
        startDate: "2026-10-01",
        endDate: "2027-01-31",
        allocationRatio: 0.2,
        plannedMM: 0.8,
      },
      {
        id: "asg_rob_yoon",
        resourceId: "res_yoon_data",
        projectRole: "수율 분석 / 재학습 파이프라인",
        accessRole: "member",
        startDate: "2026-11-01",
        endDate: "2027-04-30",
        allocationRatio: 0.5,
        plannedMM: 3.0,
      },
      {
        id: "asg_rob_seo",
        resourceId: "res_seo_ml",
        projectRole: "비전 학습 파이프라인",
        accessRole: "member",
        startDate: "2026-08-15",
        endDate: "2027-03-31",
        allocationRatio: 0.8,
        plannedMM: 6.0,
      },
      {
        id: "asg_rob_oh",
        resourceId: "res_oh_qa",
        projectRole: "시운전 품질 검수",
        accessRole: "member",
        startDate: "2026-12-01",
        endDate: "2027-04-30",
        allocationRatio: 0.5,
        plannedMM: 2.5,
      },
      {
        id: "asg_rob_min",
        resourceId: "res_min_qa",
        projectRole: "파일럿 셀 회귀 테스트",
        accessRole: "member",
        startDate: "2027-01-05",
        endDate: "2027-04-30",
        allocationRatio: 0.7,
        plannedMM: 2.8,
      },
      {
        id: "asg_rob_shin",
        resourceId: "res_shin_spec",
        projectRole: "투자·양산 자문",
        accessRole: "viewer",
        startDate: "2026-07-01",
        endDate: "2027-04-30",
        allocationRatio: 0.2,
        plannedMM: 2.0,
      },
    ],
    solutions: [
      {
        id: "sol_rob_arm",
        name: "6축 산업용 로봇 암 4식",
        category: "hardware",
        vendor: "화낙코리아",
        billingCycle: "once",
        unitCost: 85000000,
        quantity: 4,
        totalCost: 340000000,
        startDate: "2026-10-01",
        assignedResourceId: "res_han_robot",
        status: "contracted",
        notes: "파일럿 1셀 + 양산 3셀",
      },
      {
        id: "sol_rob_cam",
        name: "Cognex 3D 비전 카메라·조명 리그",
        category: "hardware",
        vendor: "코그넥스",
        billingCycle: "once",
        unitCost: 28000000,
        quantity: 4,
        totalCost: 112000000,
        startDate: "2026-10-15",
        assignedResourceId: "res_na_vision",
        status: "contracted",
      },
      {
        id: "sol_rob_gpu",
        name: "NVIDIA DGX 학습 클러스터",
        category: "hardware",
        vendor: "엔비디아",
        billingCycle: "once",
        unitCost: 180000000,
        quantity: 1,
        totalCost: 180000000,
        startDate: "2026-08-01",
        assignedResourceId: "res_seo_ml",
        status: "active",
      },
      {
        id: "sol_rob_aws",
        name: "AWS 엣지/학습 클라우드",
        category: "cloud",
        vendor: "Amazon Web Services",
        billingCycle: "monthly",
        unitCost: 12000000,
        quantity: 10,
        totalCost: 120000000,
        startDate: "2026-08-01",
        endDate: "2027-05-31",
        assignedResourceId: "res_kang_infra",
        status: "active",
      },
      {
        id: "sol_rob_mes",
        name: "Siemens Opcenter MES 모듈",
        category: "software",
        vendor: "지멘스",
        billingCycle: "yearly",
        unitCost: 72000000,
        quantity: 1,
        totalCost: 72000000,
        startDate: "2026-09-01",
        endDate: "2027-08-31",
        assignedResourceId: "res_lee_arch",
        status: "contracted",
      },
      {
        id: "sol_rob_safety",
        name: "안전 PLC·라이트커튼 패키지",
        category: "license",
        vendor: "필츠코리아",
        billingCycle: "once",
        unitCost: 26000000,
        quantity: 1,
        totalCost: 26000000,
        startDate: "2026-11-01",
        assignedResourceId: "res_baek_ctrl",
        status: "planned",
      },
    ],
    budget: {
      totalBudget: 1800000000,
      targetLaborCost: 720000000,
      targetSolutionCost: 850000000,
      targetExpenseCost: 130000000,
      contingencyCost: 100000000,
      costOptions: costOptions(),
    },
    stakeholders: [
      { id: "sh_plant", name: "박공장", org: "한빛로보틱스", role: "공장장", interest: "high", influence: "high" },
      { id: "sh_pm", name: "한로봇", org: "웨이브엑스", role: "수행 PM", interest: "high", influence: "medium" },
      { id: "sh_safety", name: "오품질", org: "한빛로보틱스", role: "안전", interest: "medium", influence: "high" },
      { id: "sh_vendor", name: "강설비", org: "세진오토메이션", role: "설치 협력", interest: "low", influence: "medium" },
    ],
    raci: [
      { stakeholderId: "sh_plant", taskId: "rob_p1", letter: "A" },
      { stakeholderId: "sh_pm", taskId: "rob_p1", letter: "R" },
      { stakeholderId: "sh_safety", taskId: "rob_p1", letter: "C" },
      { stakeholderId: "sh_pm", taskId: "rob_t1-1", letter: "A" },
      { stakeholderId: "sh_plant", taskId: "rob_t1-1", letter: "C" },
      { stakeholderId: "sh_safety", taskId: "rob_t1-1", letter: "I" },
      { stakeholderId: "sh_pm", taskId: "rob_p2", letter: "A" },
      { stakeholderId: "sh_safety", taskId: "rob_p2", letter: "C" },
      { stakeholderId: "sh_plant", taskId: "rob_p2", letter: "I" },
      { stakeholderId: "sh_pm", taskId: "rob_p3", letter: "A" },
      { stakeholderId: "sh_vendor", taskId: "rob_p3", letter: "R" },
      { stakeholderId: "sh_safety", taskId: "rob_p3", letter: "C" },
      { stakeholderId: "sh_plant", taskId: "rob_p3", letter: "I" },
      { stakeholderId: "sh_plant", taskId: "rob_p4", letter: "A" },
      { stakeholderId: "sh_safety", taskId: "rob_p4", letter: "R" },
      { stakeholderId: "sh_pm", taskId: "rob_p4", letter: "C" },
      { stakeholderId: "sh_vendor", taskId: "rob_p4", letter: "I" },
      { stakeholderId: "sh_pm", taskId: "rob_p5", letter: "A" },
      { stakeholderId: "sh_safety", taskId: "rob_p5", letter: "R" },
      { stakeholderId: "sh_plant", taskId: "rob_p5", letter: "I" },
    ],
    costActuals: [
      { id: "ca_rob_sep", periodStart: "2026-09-01", periodEnd: "2026-09-30", amount: 220_000_000, note: "자재·설치 선금" },
      { id: "ca_rob_oct", periodStart: "2026-10-01", periodEnd: "2026-10-20", amount: 18_000_000, note: "10월 공통 경비" },
    ],
    updatedAt: new Date().toISOString(),
  };
}

function sampleRobotRecords(): ProjectRecord[] {
  const at = "2026-10-10T01:00:00.000Z";
  const base = (id: string, kind: ProjectRecord["kind"], seq: number, title: string, extra: Partial<ProjectRecord>): ProjectRecord => ({
    id,
    kind,
    seq,
    title,
    status: "open",
    assignees: [],
    impacts: [],
    sections: {},
    linkedTaskIds: [],
    linkedExecutionTaskIds: [],
    linkedRecordIds: [],
    attachments: [],
    comments: [],
    activity: [{ id: `${id}_ac0`, at, actor: "나", message: "생성" }],
    createdAt: at,
    updatedAt: at,
    ...extra,
  });
  return [
    base("rob_iss_arm", "issue", 1, "1호기 로봇 암 납기 지연", {
      status: "inProgress",
      severity: "high",
      source: "vendor",
      assignees: ["한로봇", "나"],
      due: "2026-10-24",
      impacts: ["schedule"],
      updatedAt: "2026-10-18T04:00:00.000Z",
      sections: {
        summary: "화낙 1호기 슬롯이 하반기에 빠졌다. 파일럿 셀 설치가 계획보다 늦어진다.",
        cause: "벤더 라인 증설이 11월로 밀림.",
      },
      linkedTaskIds: ["rob_t3-1"],
      linkedExecutionTaskIds: ["rob_x3", "rob_x_me_arm"],
      comments: [{ id: "rob_iss_arm_cm1", author: "한로봇", body: "대체 기종 견적을 10/22에 받습니다. 파일럿만 먼저 갈지 결정이 필요합니다.", at: "2026-10-18T04:00:00.000Z" }],
    }),
    base("rob_iss_fence", "issue", 2, "안전펜스 높이 규격 미확인", {
      status: "open",
      severity: "medium",
      source: "internal",
      assignees: ["한로봇"],
      due: "2026-10-22",
      impacts: ["schedule", "quality"],
      sections: {
        summary: "안전 인증 체크리스트의 펜스 높이가 현장 실측과 맞지 않을 수 있다.",
      },
      linkedTaskIds: ["rob_t2-4"],
      linkedExecutionTaskIds: ["rob_x1", "rob_x_me_safety"],
    }),
    base("rob_iss_yield", "issue", 3, "파일럿 수율 로그 포맷 불일치", {
      status: "resolved",
      severity: "low",
      source: "internal",
      assignees: ["윤데이터"],
      due: "2026-10-08",
      closedAt: "2026-10-09T06:00:00.000Z",
      sections: {
        summary: "엣지 게이트웨이 로그와 학습 파이프라인 스키마가 달랐다.",
        resolution: "공통 스키마 v0.3으로 맞추고 10/9 재적재했다.",
      },
      linkedTaskIds: ["rob_t3-4"],
    }),
    base("rob_rsk_data", "risk", 1, "비전 학습 데이터가 2천 장에 못 미칠 수 있음", {
      status: "identified",
      severity: "high",
      source: "internal",
      assignees: ["나비전"],
      due: "2026-11-07",
      impacts: ["quality", "schedule"],
      sections: {
        summary: "불량 클래스가 한쪽으로 몰리면 수율 튜닝이 양산 전에 끝나지 않는다.",
        mitigation: "양품·불량 비율을 먼저 합의하고, 부족한 클래스는 합성으로 채운다.",
      },
      linkedTaskIds: ["rob_t2-2", "rob_t4-2"],
      linkedExecutionTaskIds: ["rob_x2", "rob_x_vision_week"],
    }),
    base("rob_rsk_gpu", "risk", 2, "학습 클러스터 반입 검사 지연", {
      status: "mitigating",
      severity: "medium",
      source: "vendor",
      assignees: ["서머신"],
      due: "2026-10-31",
      impacts: ["schedule"],
      sections: {
        summary: "DGX 반입 검사가 고객사 보안 절차에 걸려 학습 시작이 늦어질 수 있다.",
        mitigation: "클라우드 임시 학습으로 모델 초안을 먼저 돌린다.",
      },
      linkedTaskIds: ["rob_t3-4"],
    }),
    base("rob_dec_pilot", "decision", 1, "파일럿 1셀을 양산 3셀보다 먼저 연다", {
      status: "proposed",
      source: "internal",
      assignees: ["한로봇", "나"],
      due: "2026-10-24",
      impacts: ["schedule", "cost"],
      sections: {
        context: "1호기 납기가 밀리면 4셀을 같이 열 수 없다.",
        decision: "파일럿 1셀만 11월에 시운전하고, 나머지 3셀은 납기가 확정된 뒤 연다.",
        alternatives: "4셀 동시 — 납기가 한 대라도 밀리면 전체가 선다.",
      },
      linkedTaskIds: ["rob_t3-1", "rob_t4-1"],
      linkedRecordIds: ["rob_iss_arm"],
    }),
    base("rob_pol_stop", "policy", 1, "라인 비상 정지 후 재가동은 2인 확인", {
      status: "accepted",
      source: "regulation",
      assignees: ["백제어"],
      due: "2026-11-15",
      sections: {
        statement: "비상 정지 뒤에는 제어 담당과 안전 담당이 각각 확인한 뒤에만 재가동한다.",
        applicability: "파일럿 셀과 양산 셀 전체.",
      },
      linkedTaskIds: ["rob_t2-3"],
    }),
    base("rob_chg_cert", "change", 1, "안전 인증 범위를 파일럿 셀까지 확대", {
      status: "reviewing",
      severity: "medium",
      source: "client",
      assignees: ["한로봇"],
      due: "2026-10-29",
      impacts: ["scope", "schedule"],
      sections: {
        summary: "고객사가 파일럿 셀도 양산과 같은 안전 인증을 요구했다.",
        reason: "현장 출입 기준을 셀마다 나누지 않기로 함.",
        impactAnalysis: "인증 체크리스트 항목 약 20개 추가. 설계 리뷰 3일.",
      },
      linkedTaskIds: ["rob_t2-4"],
      linkedExecutionTaskIds: ["rob_x1"],
    }),
  ];
}
